import React from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowLeft } from 'lucide-react';

const KesarGuide = () => {
  const saffronFeatures = [
    'Grade-1 Premium Quality',
    'Sourced from Kashmir Valley',
    '100% Pure and Natural',
    'No Artificial Colors or Additives',
    'Rich Aroma and Deep Color',
    'Carefully Hand-Picked',
    'Lab Tested for Purity',
    'Authentic Indian Origin'
  ];

  return (
    <div className="pt-16 sm:pt-20">
      {/* Header Section */}
      <section className="bg-gradient-to-br from-saffron-50 to-cream-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Link
              to="/products/kesar"
              className="inline-flex items-center gap-2 text-saffron-600 hover:text-saffron-700 font-medium mb-6 transition-colors duration-200"
              data-testid="back-to-kesar-btn"
            >
              <ArrowLeft size={18} />
              Back to Kesar Product
            </Link>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4" data-testid="guide-page-title">
              Premium Kashmiri Kesar
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to know about the world&#8217;s finest saffron, sourced from the pristine valleys of Kashmir
            </p>
          </div>
        </div>
      </section>

      {/* Product Overview Section */}
      <section className="py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Product Image */}
            <div className="order-2 lg:order-1">
              <div className="relative">
                <img
                  src="https://customer-assets-7cd3h4nn.emergentagent.net/job_828e32ce-193c-4c51-b926-dcf4aea89e6f/artifacts/ocion9gb_WhatsApp%20Image%202026-07-24%20at%2010.45.41%20AM.jpeg"
                  alt="Hind Harvest Kashmiri Kesar"
                  className="rounded-2xl shadow-2xl w-full object-contain bg-cream-100"
                  data-testid="guide-product-image"
                />
              </div>
            </div>

            {/* Product Details */}
            <div className="order-1 lg:order-2">
              <div className="inline-block bg-saffron-100 text-saffron-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                Kesar Guide
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Premium Kashmiri Saffron</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Our signature product &mdash; the world&#8217;s finest saffron from the pristine valleys of Kashmir.
                Known for its deep color, rich aroma, and unmatched quality, this premium grade saffron
                is carefully hand-picked and processed to preserve its natural properties.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Perfect for culinary excellence, traditional medicine, and adding a touch of luxury to
                your daily life. Each strand is a testament to centuries of tradition and expertise.
              </p>

              {/* Features Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {saffronFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-5 h-5 bg-saffron-100 rounded-full flex items-center justify-center mt-0.5">
                      <Check className="text-saffron-600" size={14} />
                    </div>
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <Link
                to="/products/kesar"
                className="inline-flex items-center justify-center px-8 py-4 bg-saffron-600 text-white font-semibold rounded-lg hover:bg-saffron-700 transition-all duration-300 hover:shadow-lg hover:scale-105"
                data-testid="guide-order-btn"
              >
                Order Kesar
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Our Saffron Stands Out */}
      <section className="py-20 bg-cream-50" data-testid="why-stands-out-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Why Our Saffron Stands Out</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h4 className="text-xl font-semibold text-gray-900 mb-3">Origin &amp; Authenticity</h4>
                <p className="text-gray-600 leading-relaxed">
                  Sourced directly from Kashmir&#8217;s renowned saffron fields, where the unique climate
                  and soil produce the world&#8217;s finest saffron threads.
                </p>
              </div>
              <div>
                <h4 className="text-xl font-semibold text-gray-900 mb-3">Processing &amp; Care</h4>
                <p className="text-gray-600 leading-relaxed">
                  Hand-picked at dawn and carefully dried using traditional methods to preserve
                  maximum flavor, aroma, and medicinal properties.
                </p>
              </div>
              <div>
                <h4 className="text-xl font-semibold text-gray-900 mb-3">Quality Assurance</h4>
                <p className="text-gray-600 leading-relaxed">
                  Every batch is rigorously tested for purity and quality, ensuring you receive
                  only the finest Grade-1 saffron threads.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Health Benefits Section */}
      <section className="py-20 bg-white" data-testid="health-benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Health Benefits of Pure Kesar</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Clinically proven wellness benefits backed by modern science.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Benefit 1 - Happier Mind */}
            <Link
              to="/articles/pure-kashmiri-kesar-clinically-proven-elixir#happier-mind"
              className="bg-cream-50 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
            >
              <div className="h-48 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1687436874774-fa0d4616bec9"
                  alt="Mental Wellness"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Happier Mind, Naturally</h3>
                <p className="text-gray-600 leading-relaxed">
                  Clinically proven to improve mood and reduce symptoms of mild-to-moderate depression comparable to standard antidepressants.
                </p>
              </div>
            </Link>

            {/* Benefit 2 - Sharper Memory */}
            <Link
              to="/articles/pure-kashmiri-kesar-clinically-proven-elixir#sharper-memory"
              className="bg-cream-50 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
            >
              <div className="h-48 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_harvest-india/artifacts/0xovdu70_Sharper%20Memory%20%26%20Focus.jpg"
                  alt="Cognitive Health"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Sharper Memory &amp; Focus</h3>
                <p className="text-gray-600 leading-relaxed">
                  Shown in human trials to enhance memory, learning, and cognitive performance, including support in age-related decline.
                </p>
              </div>
            </Link>

            {/* Benefit 3 - Stress Relief */}
            <Link
              to="/articles/pure-kashmiri-kesar-clinically-proven-elixir#stress-relief"
              className="bg-cream-50 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
            >
              <div className="h-48 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_harvest-india/artifacts/iqf7kiem_Stress%20%26%20Anxiety%20Relief.jpg"
                  alt="Stress Relief"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Stress &amp; Anxiety Relief</h3>
                <p className="text-gray-600 leading-relaxed">
                  Demonstrated to lower anxiety levels and promote calm by balancing stress-related neurotransmitters.
                </p>
              </div>
            </Link>

            {/* Benefit 4 - Heart Health */}
            <Link
              to="/articles/pure-kashmiri-kesar-clinically-proven-elixir#heart-health"
              className="bg-cream-50 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
            >
              <div className="h-48 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_harvest-india/artifacts/p9ram2vb_Heart-Healthy%20Protection.png"
                  alt="Heart Health"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Heart-Healthy Protection</h3>
                <p className="text-gray-600 leading-relaxed">
                  Clinically shown to reduce LDL oxidation and improve antioxidant status, supporting healthier blood vessels and circulation.
                </p>
              </div>
            </Link>

            {/* Benefit 5 - PMS Comfort */}
            <Link
              to="/articles/pure-kashmiri-kesar-clinically-proven-elixir#pms-comfort"
              className="bg-cream-50 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
            >
              <div className="h-48 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_harvest-india/artifacts/txomo2g1_PMS%20%26%20Menstrual%20Comfort.jpg"
                  alt="Women's Health"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">PMS &amp; Menstrual Comfort</h3>
                <p className="text-gray-600 leading-relaxed">
                  Proven to significantly reduce PMS symptoms like cramps, mood swings, and irritability in women.
                </p>
              </div>
            </Link>

            {/* Benefit 6 - Radiant Skin */}
            <Link
              to="/articles/pure-kashmiri-kesar-clinically-proven-elixir#radiant-skin"
              className="bg-cream-50 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
            >
              <div className="h-48 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_harvest-india/artifacts/ilfh3prs_luxury_final_fixed_Radiant_skin_png.png"
                  alt="Radiant Skin"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Radiant Skin From Within</h3>
                <p className="text-gray-600 leading-relaxed">
                  Rich in potent antioxidants that protect cells from oxidative damage, helping maintain clearer, healthier-looking skin.
                </p>
              </div>
            </Link>

            {/* Benefit 7 - Immunity Support */}
            <Link
              to="/articles/pure-kashmiri-kesar-clinically-proven-elixir#immunity-support"
              className="bg-cream-50 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
            >
              <div className="h-48 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_harvest-india/artifacts/wn7zw1hf_luxury_stronger_immunity.jpg"
                  alt="Immunity Support"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Stronger Immunity Support</h3>
                <p className="text-gray-600 leading-relaxed">
                  Scientifically validated antioxidant and anti-inflammatory activity helps strengthen the body&#8217;s natural defense system.
                </p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* How to Check Purity Section */}
      <section className="py-20 bg-cream-50" data-testid="purity-check-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">How to Check the Purity of Kesar</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Learn how to identify authentic saffron and ensure you&#8217;re getting the best quality product.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {/* Step 1 - Water Test */}
            <div className="bg-white rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="mb-6 rounded-xl overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_harvest-india/artifacts/ndigemud_final_test_water_test_yellow.jpeg"
                  alt="Saffron Water Test"
                  className="w-full h-48 object-cover"
                />
              </div>
              <div className="flex items-center mb-3">
                <span className="w-8 h-8 bg-saffron-600 text-white rounded-full flex items-center justify-center font-bold text-sm mr-3">1</span>
                <h3 className="text-xl font-bold text-gray-900">Water Test</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Place a few strands in warm water. Pure saffron releases color slowly while the threads keep their shape.
              </p>
            </div>

            {/* Step 2 - Color Check */}
            <div className="bg-white rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="mb-6 rounded-xl overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_harvest-india/artifacts/exxcnkhc_final_test_Saffron_Color_Check1_png.png"
                  alt="Saffron Color Check"
                  className="w-full h-48 object-cover"
                />
              </div>
              <div className="flex items-center mb-3">
                <span className="w-8 h-8 bg-saffron-600 text-white rounded-full flex items-center justify-center font-bold text-sm mr-3">2</span>
                <h3 className="text-xl font-bold text-gray-900">Color Check</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Authentic saffron is deep red with slightly orange tips. Avoid bright yellow or overly uniform strands.
              </p>
            </div>

            {/* Step 3 - Aroma Test */}
            <div className="bg-white rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="mb-6 rounded-xl overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_harvest-india/artifacts/ltkcl1lh_final_test_saffron_aroma_test.png"
                  alt="Saffron Aroma Test"
                  className="w-full h-48 object-cover"
                />
              </div>
              <div className="flex items-center mb-3">
                <span className="w-8 h-8 bg-saffron-600 text-white rounded-full flex items-center justify-center font-bold text-sm mr-3">3</span>
                <h3 className="text-xl font-bold text-gray-900">Aroma Test</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Pure saffron has a strong natural aroma &mdash; earthy and honey-like, not artificial or sweet.
              </p>
            </div>

            {/* Step 4 - Taste Test */}
            <div className="bg-white rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="mb-6 rounded-xl overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_harvest-india/artifacts/ubtmmbh3_final_test_taste_testing.png"
                  alt="Saffron Taste Test"
                  className="w-full h-48 object-cover"
                />
              </div>
              <div className="flex items-center mb-3">
                <span className="w-8 h-8 bg-saffron-600 text-white rounded-full flex items-center justify-center font-bold text-sm mr-3">4</span>
                <h3 className="text-xl font-bold text-gray-900">Taste Test</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Real saffron tastes slightly bitter, never sweet.
              </p>
            </div>

            {/* Step 5 - Thread Structure */}
            <div className="bg-white rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="mb-6 rounded-xl overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_harvest-india/artifacts/0hcm24vy_final_test_Thread_Structure_Test.png"
                  alt="Saffron Thread Structure"
                  className="w-full h-48 object-cover"
                />
              </div>
              <div className="flex items-center mb-3">
                <span className="w-8 h-8 bg-saffron-600 text-white rounded-full flex items-center justify-center font-bold text-sm mr-3">5</span>
                <h3 className="text-xl font-bold text-gray-900">Thread Structure</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Original strands are trumpet-shaped and uneven, not perfectly straight or identical.
              </p>
            </div>
          </div>

          {/* Guarantee Statement */}
          <div className="bg-gradient-to-br from-saffron-50 to-cream-100 rounded-2xl p-8 md:p-12 text-center">
            <p className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
              Hind Harvest guarantees 100% pure, lab-tested saffron sourced directly from trusted farms.
            </p>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Ready to Experience Premium Kesar?</h3>
          <p className="text-gray-600 mb-8">Order authentic Kashmiri saffron directly from Hind Harvest.</p>
          <Link
            to="/products/kesar"
            className="inline-flex items-center justify-center px-10 py-4 bg-saffron-600 text-white font-semibold rounded-lg hover:bg-saffron-700 transition-all duration-300 hover:shadow-lg hover:scale-105 text-lg"
            data-testid="guide-bottom-cta"
          >
            Back to Kesar Product
          </Link>
        </div>
      </section>
    </div>
  );
};

export default KesarGuide;
